import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { useToast } from "@/shared/hooks/use-toast";
import { useBrandAssets } from "@/entities/brand-asset";
import { cn } from "@/shared/lib/utils";
import { UrlQrPreview } from "@/shared/ui/UrlQrPreview";
import { pickDefaultLogoPosition, pickDefaultQrPosition } from "../../../model/pickDefaultOverlay";
import type { OverlayConfig as OverlayConfigV6 } from "../../../api/posterTemplateService";
import { BrandAssetPicker, type BrandAssetOption } from "./BrandAssetPicker";

/**
 * 「挂 logo / 挂二维码」两颗按钮(2026-08-08 从 AssetEditor 抽出)。
 *
 * ★抽出来的原因是**长图那边也要用**:整图品牌入口砍掉之后,块级编辑器成了唯一的挂载路径,
 *   而它此前只会读 `overlay.logoUploadId`、根本没有挑选入口 —— 画布上永远没东西可拖。
 *   留在 AssetEditor 里 export 出去也行,但那个文件已经 1000+ 行,而这两颗按钮
 *   和 {@link BrandAssetPicker} 本来就是一组。
 *
 * ★选择器换成网格 + 搜索(见 BrandAssetPicker 的注释):用户报的「logo 太多无法选择」
 *   就是原来那个 32px 缩略图 + 截断名字的纵向列表。
 */

const dashedClass =
  "w-full rounded border border-dashed border-border/60 px-2 py-1.5 text-2xs text-muted-foreground hover:border-primary/50 hover:text-foreground";

export function AttachLogoButton({
  value,
  onChange,
  canvasAspect,
}: {
  value: OverlayConfigV6;
  onChange: (next: OverlayConfigV6) => void;
  canvasAspect: number;
}) {
  const { t } = useTranslation("poster");
  const { toast } = useToast();
  const { data: brandAssets, isLoading } = useBrandAssets();
  const [open, setOpen] = useState(false);
  const logos = brandAssets?.logos ?? [];

  /* ★2026-08-28 修「挂了 logo 点不掉」:此前这两颗按钮**只写不清** —— 单 logo 直接挂、
     多 logo 走 picker 挂,但没有任何一条分支把 `logoUploadId` 置空,用户报的「点了无法取消」
     就是这个。已挂状态(logoUploadId 非空)优先给一颗「移除」按钮,再点即摘掉(logo/logoUploadId
     一起清）。放在最前面还顺带救一种边角:logo 已从品牌库删掉但 overlay 仍引用它时,
     原来会落到 length===0 的「还没上传」分支、永远摘不掉。 */
  if (value.logoUploadId != null) {
    return (
      <button
        type="button"
        onClick={() => onChange({ ...value, logo: null, logoUploadId: null })}
        className={dashedClass}
      >
        × {t("editor.removeLogo")}
      </button>
    );
  }

  if (isLoading) {
    return (
      <button type="button" disabled className={cn(dashedClass, "opacity-50")}>
        {t("editor.attachLogo")}…
      </button>
    );
  }
  if (logos.length === 0) {
    return (
      <button
        type="button"
        onClick={() =>
          toast({
            title: t("editor.noLogoUploaded"),
            description: t("editor.uploadInBrandAssetsPage"),
          })
        }
        className={dashedClass}
      >
        + {t("editor.attachLogo")}
      </button>
    );
  }
  /* 只有一个就直接挂 —— 为一个选项开一个选择器是纯粹的多余一步。 */
  if (logos.length === 1) {
    return (
      <button
        type="button"
        onClick={() =>
          onChange({
            ...value,
            logo: pickDefaultLogoPosition(canvasAspect, logos[0].aspect),
            logoUploadId: logos[0].objectKey,
          })
        }
        className={dashedClass}
      >
        + {t("editor.attachLogo")}
      </button>
    );
  }

  const options: BrandAssetOption[] = logos.map((l) => ({
    id: l.objectKey,
    name: l.name,
    isDefault: l.isDefault,
    signedUrl: l.signedUrl,
    colorHex: l.colorHex,
  }));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={dashedClass}>
          + {t("editor.attachLogo")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <p className="mb-2 px-1 text-2xs text-muted-foreground">{t("editor.pickLogoToAttach")}</p>
        <BrandAssetPicker
          options={options}
          selectedId={value.logoUploadId ?? null}
          searchPlaceholder="搜 logo 名字…"
          onPick={(o) => {
            const hit = logos.find((l) => l.objectKey === o.id);
            onChange({
              ...value,
              logo: pickDefaultLogoPosition(canvasAspect, hit?.aspect),
              logoUploadId: o.id,
            });
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export function AttachQrButton({
  value,
  onChange,
  canvasAspect,
}: {
  value: OverlayConfigV6;
  onChange: (next: OverlayConfigV6) => void;
  canvasAspect: number;
}) {
  const { t } = useTranslation("poster");
  const { toast } = useToast();
  const { data: brandAssets, isLoading } = useBrandAssets();
  const [open, setOpen] = useState(false);
  const qrs = brandAssets?.qrs ?? [];

  /** UPLOAD 与 URL 两档写的是不同的字段 —— 只改一半会留下上一档的残值。 */
  const attachById = (id: string) => {
    const qr = qrs.find((q) => (q.objectKey ?? q.url ?? q.id) === id);
    if (!qr) return;
    const pos = pickDefaultQrPosition(canvasAspect);
    onChange(
      qr.mode === "URL"
        ? { ...value, qr: pos, qrCodeMode: "URL", qrCodeUrl: qr.url ?? null, qrCodeUploadId: null }
        : {
            ...value,
            qr: pos,
            qrCodeMode: "UPLOAD",
            qrCodeUploadId: qr.objectKey ?? null,
            qrCodeUrl: null,
          }
    );
    setOpen(false);
  };

  /* ★2026-08-28 同 logo:已挂二维码(UPLOAD/URL 任一档留了值)优先给「移除」,再点即摘掉,
     三个 qrCode* 字段连同 qr bbox 一起清 —— 否则残值会让下次挂另一档时串档。 */
  if (value.qrCodeMode != null || value.qrCodeUploadId != null || value.qrCodeUrl != null) {
    return (
      <button
        type="button"
        onClick={() =>
          onChange({
            ...value,
            qr: null,
            qrCodeMode: null,
            qrCodeUploadId: null,
            qrCodeUrl: null,
          })
        }
        className={dashedClass}
      >
        × {t("editor.removeQr")}
      </button>
    );
  }

  if (isLoading) {
    return (
      <button type="button" disabled className={cn(dashedClass, "opacity-50")}>
        {t("editor.attachQr")}…
      </button>
    );
  }
  if (qrs.length === 0) {
    return (
      <button
        type="button"
        onClick={() =>
          toast({
            title: t("editor.noQrUploaded"),
            description: t("editor.uploadInBrandAssetsPage"),
          })
        }
        className={dashedClass}
      >
        + {t("editor.attachQr")}
      </button>
    );
  }
  if (qrs.length === 1) {
    return (
      <button
        type="button"
        onClick={() => attachById(qrs[0].objectKey ?? qrs[0].url ?? qrs[0].id)}
        className={dashedClass}
      >
        + {t("editor.attachQr")}
      </button>
    );
  }

  const options: BrandAssetOption[] = qrs.map((q) => ({
    id: q.objectKey ?? q.url ?? q.id,
    name: q.name,
    isDefault: q.isDefault,
    signedUrl: q.mode === "UPLOAD" ? q.signedUrl : null,
    colorHex: q.colorHex,
    badge: q.mode === "UPLOAD" ? "上传" : "链接",
    // URL 档没有图片,现场按链接生成一张 —— 否则一排灰块认不出哪个是哪个
    thumb:
      q.mode === "URL" && q.url ? (
        <UrlQrPreview url={q.url} size={56} className="h-14 w-14 rounded" />
      ) : undefined,
  }));

  // 走到这里 = 上面「已挂→移除」守卫没命中 ⇒ 三个 qrCode* 都为 null(TS 已收窄),
  // 即当前没挂二维码,picker 无预选。原来的 qrCodeMode==="URL" 分支在守卫后成死路(TS2367)。
  const selected = null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={dashedClass}>
          + {t("editor.attachQr")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <p className="mb-2 px-1 text-2xs text-muted-foreground">{t("editor.pickQrToAttach")}</p>
        <BrandAssetPicker
          options={options}
          selectedId={selected}
          searchPlaceholder="搜二维码名字…"
          onPick={(o) => attachById(o.id)}
        />
      </PopoverContent>
    </Popover>
  );
}
